// LAS PORTADAS VIVEN AQUI, y antes se le pedian a `images.unsplash.com` en cada visita.
// Las de los cursos se ven ademas en la pantalla de inicio, o sea sin tener cuenta: la IP
// de cada visitante llegaba a un servidor ajeno antes de que nadie consintiera nada. Van importadas
// y no como texto a proposito -un fichero que no existe revienta el build en vez de verse
// como un hueco gris-. La procedencia y la licencia, en `portadas/PROCEDENCIA.md`.
// El Mentor Socratico y Psicologia comparten foto con un curso, y el nombre de la variable
// lo dice: es la misma imagen y un solo fichero, no dos copias que se separen sin querer.
// El marmol griego vive en `cultura-clasica.jpg` y no en `filosofia.jpg`, que es donde
// estaba: Cultura Clasica se anunciaba con un monitor y un teclado.
import portadaPsicologia from "@/portadas/psicologia.webp";
import portadaConstelaciones from "@/portadas/constelaciones-alpha.webp";
import portadaBibliotecaViva from "@/portadas/biblioteca-viva.webp";
import portadaCulturaClasica from "@/portadas/cultura-clasica.webp";
import portadaTallerEscritura from "@/portadas/taller-escritura.webp";
import portadaPlanificador from "@/portadas/planificador-alpha.webp";
import portadaRepeticion from "@/portadas/repeticion-inteligente.webp";
import portadaGeneradorIdeas from "@/portadas/generador-ideas.webp";
import portadaAnalitica from "@/portadas/analitica-cognitiva.webp";

// LAS PIEZAS DEL METODO, Y SE LLAMAN SISTEMAS. Esto se exportaba como `MODULES`, y era la
// mitad del problema que el glosario venia arrastrando: «modulo» significaba a la vez esto
// -producto- y un bloque de temario -curriculo-. El 6 de septiembre se resolvio al reves de
// como se habia decidido en agosto: **modulo es el bloque de temario**, que es como lo
// llamaban ya el codigo (`modulo.txt`, campo `module`) y las conversaciones, y estas piezas
// pasan a llamarse por el nombre que llevan en la web desde el principio, «los 6 sistemas
// Alpha». La jerarquia entera esta en `docs/estado/glosario.md`.
export const SISTEMAS = [
  {
    slug: "memoria-alpha",
    key: "memoria",
    number: "01",
    name: "Memoria Alpha",
    tag: "Cognición",
    tagline: "La IA deja de recordar conversaciones para empezar a recordar personas.",
    summary: "Memoria intelectual viva que reconstruye tu identidad como pensador a lo largo de los años, no de las últimas conversaciones.",
    topics: [
      { name: "Memoria episódica", def: "Sistema de memoria consciente que almacena experiencias autobiográficas contextualizadas.", ref: "Tulving, 1972" },
      { name: "Active Recall", def: "Recuperación activa de información desde la memoria; multiplica la retención frente a la relectura pasiva.", ref: "Roediger & Karpicke, 2006" },
      { name: "Consolidación", def: "Proceso neural que estabiliza recuerdos en memoria a largo plazo, ligado al sueño y a la repetición espaciada.", ref: "McGaugh, 2000" },
      { name: "Metacognición", def: "Conocimiento sobre el propio proceso cognitivo. Piedra angular del aprender a aprender.", ref: "Flavell, 1979" },
      { name: "Elaborative Learning", def: "Estrategia de codificación profunda mediante conexión con conocimientos previos.", ref: "Craik & Lockhart, 1972" },
    ],
    image: portadaPsicologia,
    color: "#0B0B0B",
  },
  {
    slug: "constelaciones-alpha",
    key: "constelaciones",
    number: "02",
    name: "Constelaciones Alpha",
    tag: "Conocimiento",
    tagline: "Cada concepto deja de ser una unidad aislada para volverse un nodo vivo.",
    summary: "El curso como recorrido: cada lección es una estrella que se desbloquea, agrupada en constelaciones por temática. Y el mapa de tu memoria, que se dibuja con lo que estudias. La arquitectura del conocimiento.",
    topics: [
      { name: "Constructivismo", def: "El conocimiento se construye activamente por el sujeto a través de la experiencia.", ref: "Piaget, 1970" },
      { name: "Conectivismo", def: "Teoría del aprendizaje que entiende el saber como una red dinámica de conexiones.", ref: "Siemens, 2005" },
      { name: "Aprendizaje por dominio", def: "Cada estudiante consolida un nivel antes de abrir el siguiente.", ref: "Bloom, 1968" },
      { name: "Aprendizaje significativo", def: "Aprender integrando nueva información con estructuras cognitivas previas.", ref: "Ausubel, 1968" },
    ],
    image: portadaConstelaciones,
    color: "#0B0B0B",
  },
  {
    slug: "mentor-socratico",
    key: "mentor",
    number: "03",
    name: "Mentor Socrático",
    tag: "Diálogo",
    tagline: "El objetivo no es que dependas de la IA, sino que aprendas a pensar con autonomía.",
    summary: "El corazón pedagógico. Un mentor IA que provoca el pensamiento a través de la mayéutica y la transformación de las preguntas.",
    topics: [
      { name: "Mayéutica", def: "Método socrático de hacer emerger el conocimiento a través de preguntas.", ref: "Platón, s. IV a.C." },
      { name: "Pedagogía crítica", def: "Enseñar como acto político-emancipador que problematiza el mundo.", ref: "Freire, 1970" },
      { name: "Conflicto cognitivo", def: "Desequilibrio productivo entre lo que se sabe y lo que se descubre.", ref: "Piaget, 1975" },
      { name: "Autorregulación", def: "Capacidad de planificar, monitorear y ajustar el propio aprendizaje.", ref: "Zimmerman, 2002" },
    ],
    image: portadaCulturaClasica,
    color: "#0B0B0B",
  },
  {
    slug: "biblioteca-viva",
    key: "biblioteca",
    number: "04",
    name: "Biblioteca Viva",
    tag: "Recursos",
    tagline: "Una biblioteca que aprende contigo.",
    summary: "Sistema inteligente de gestión del conocimiento. Los recursos dialogan entre sí y evolucionan con tu proceso.",
    topics: [
      { name: "Gestión del conocimiento", def: "Prácticas de creación, organización y transferencia de saberes.", ref: "Nonaka & Takeuchi, 1995" },
      { name: "Humanidades digitales", def: "Intersección entre humanidades y tecnologías digitales para investigación y difusión.", ref: "Schreibman et al., 2004" },
      { name: "Recuperación contextual", def: "Modelos que priorizan la relevancia según contexto del usuario.", ref: "Belkin & Croft, 1992" },
      { name: "Recursos vivos", def: "Materiales que evolucionan con el uso y aportan nueva información en cada lectura.", ref: "Papert, 1980" },
    ],
    image: portadaBibliotecaViva,
    color: "#0B0B0B",
  },
  {
    slug: "taller-escritura",
    key: "escritura",
    number: "05",
    name: "Espacio de escritura colectiva",
    tag: "Producción",
    tagline: "La escritura como forma de pensar.",
    summary: "Una consigna cada semana, un texto breve, y el Ágora leyéndolo. Sócrates lo corona con una insignia —ingenio, profundidad, estilo o riesgo—, que son cuatro maneras de escribir bien y no una nota. Un texto que alguien va a leer se escribe distinto.",
    topics: [
      { name: "Retórica", def: "Arte del uso persuasivo y estético del lenguaje.", ref: "Aristóteles, s. IV a.C." },
      { name: "Análisis estilístico", def: "Estudio de los rasgos formales que definen la voz de un autor.", ref: "Spitzer, 1948" },
      { name: "Argumentación", def: "Construcción lógica y dialéctica de razones para sostener una tesis.", ref: "Toulmin, 1958" },
      { name: "Voz autoral", def: "Firma singular del sujeto que escribe, entre estilo y subjetividad.", ref: "Barthes, 1968" },
      { name: "Aprendizaje entre iguales", def: "Leer y responder al texto de un compañero enseña tanto como escribir el propio.", ref: "Topping, 2005" },
    ],
    image: portadaTallerEscritura,
    color: "#0B0B0B",
  },
  {
    slug: "planificador-alpha",
    key: "planificador",
    number: "06",
    name: "Planificador Alpha",
    tag: "Estrategia",
    tagline: "¿Cuál es la próxima acción que generará el mayor crecimiento intelectual posible?",
    summary: "Itinerarios contrastados con el currículo estatal: cada lección responde a una competencia, un saber y un criterio de evaluación del BOE. El test propone por dónde empezar. Crecimiento intelectual.",
    topics: [
      { name: "Aprendizaje autorregulado", def: "El estudiante planifica, ejecuta y evalúa su propio proceso.", ref: "Pintrich, 2000" },
      { name: "Mastery learning", def: "Cada estudiante domina un nivel antes de avanzar al siguiente.", ref: "Bloom, 1968" },
      { name: "Diseño curricular", def: "Secuencia de objetivos, contenidos y criterios que ordena una materia.", ref: "Tyler, 1949" },
      { name: "Agentes autónomos", def: "Software que percibe, decide y actúa sin supervisión constante.", ref: "Russell & Norvig, 2010" },
    ],
    image: portadaPlanificador,
    color: "#0B0B0B",
  },
  {
    slug: "repeticion-inteligente",
    key: "repeticion",
    number: "07",
    name: "Repetición Inteligente",
    tag: "Consolidación",
    tagline: "El sistema propone la actividad que maximiza la consolidación de cada concepto.",
    summary: "Va más allá de la repetición espaciada tradicional: personaliza experiencias de recuperación activa según toda tu historia de aprendizaje.",
    topics: [
      { name: "Curva del olvido", def: "Decaimiento exponencial de la retención sin refuerzo.", ref: "Ebbinghaus, 1885" },
      { name: "Recuperación activa", def: "Traer la información a la mente sin ayudas externas.", ref: "Karpicke, 2012" },
      { name: "Interleaving", def: "Alternar tipos de problemas para aumentar la transferencia.", ref: "Rohrer & Taylor, 2007" },
      { name: "Dificultad deseable", def: "Esfuerzo cognitivo óptimo que favorece la memoria a largo plazo.", ref: "Bjork, 1994" },
    ],
    image: portadaRepeticion,
    color: "#0B0B0B",
  },
  {
    slug: "generador-ideas",
    key: "ideas",
    number: "08",
    name: "Generador de Ideas",
    tag: "Creatividad",
    tagline: "¿Qué podrías descubrir con todo lo que sabes?",
    summary: "El motor de creatividad. Identifica relaciones latentes en tu conocimiento y las transforma en nuevas hipótesis, proyectos y soluciones.",
    topics: [
      { name: "Pensamiento divergente", def: "Capacidad de generar múltiples respuestas válidas a un problema.", ref: "Guilford, 1967" },
      { name: "Creatividad combinatoria", def: "Nuevas ideas surgen de conexiones entre dominios conocidos.", ref: "Boden, 1990" },
      { name: "Conexiones transversales", def: "Vínculos entre disciplinas que originan innovación.", ref: "Koestler, 1964" },
      { name: "Innovación", def: "Traducción de una idea nueva en valor social, científico o estético.", ref: "Schumpeter, 1942" },
    ],
    image: portadaGeneradorIdeas,
    color: "#D10000",
  },
  {
    slug: "adaptacion-alpha",
    key: "adaptacion",
    number: "10",
    name: "Adaptación Alpha",
    tagline: "La plataforma se ajusta al alumno, no al revés",
    summary: "Cada alumno decide cómo quiere estudiar —movimiento, texto, contraste, sonido, reloj— y la plataforma entera obedece. Los ajustes se describen por lo que hacen, nunca por a quién van dirigidos: no hay «modo dislexia», hay «letra más legible».",
    topics: [
      { name: "Diseño Universal para el Aprendizaje", def: "Diseñar desde el principio para la diversidad, en vez de adaptar después caso por caso.", ref: "CAST, 2011" },
      { name: "Carga cognitiva", def: "La memoria de trabajo es limitada: lo que distrae ocupa el sitio de lo que se aprende.", ref: "Sweller, 1988" },
      { name: "Accesibilidad web", def: "Criterios verificables de contraste, movimiento y legibilidad para contenido digital.", ref: "W3C, WCAG 2.2" },
      { name: "Autodeterminación", def: "La autonomía sobre el propio entorno de trabajo sostiene la motivación intrínseca.", ref: "Deci & Ryan, 1985" },
    ],
    image: portadaAnalitica,
  },
  {
    slug: "el-cielo",
    key: "cielo",
    number: "11",
    name: "El Cielo",
    tagline: "El curso como recorrido, no como lista",
    summary: "Cada lección es una estrella de un mapa, y las estrellas se desbloquean unas a otras: el orden en que se aprende es una decisión pedagógica tomada de antemano. Los módulos del temario son constelaciones, y el recorrido acaba en un Final Boss.",
    topics: [
      { name: "Zona de desarrollo próximo", def: "Se aprende justo por encima de lo que ya se domina, no muy por encima.", ref: "Vygotsky, 1978" },
      { name: "Currículo en espiral", def: "Los mismos conceptos vuelven con más exigencia en cada vuelta.", ref: "Bruner, 1960" },
      { name: "Flujo", def: "La concentración aparece cuando el reto y la destreza van a la par.", ref: "Csikszentmihalyi, 1990" },
      { name: "Secuenciación del aprendizaje", def: "El orden de los contenidos es en sí mismo una decisión didáctica.", ref: "Gagné, 1985" },
    ],
    image: portadaConstelaciones,
  },
  {
    slug: "analitica-cognitiva",
    key: "analitica",
    number: "09",
    name: "Analítica Cognitiva",
    tag: "Métrica",
    tagline: "No se trata de medir resultados. Se trata de entender el proceso de transformación intelectual.",
    summary: "El cerebro metacognitivo. Transforma los datos de aprendizaje en inteligencia accionable sobre tu desarrollo intelectual.",
    topics: [
      { name: "Learning analytics", def: "Medición y análisis de datos de aprendizaje para mejorar procesos y contextos.", ref: "Siemens, 2010" },
      { name: "Evaluación formativa", def: "Evaluación que retroalimenta el aprendizaje en tiempo real.", ref: "Black & Wiliam, 1998" },
      { name: "Modelo dinámico", def: "Representación en evolución del estado cognitivo del estudiante.", ref: "VanLehn, 2011" },
      { name: "Competencias complejas", def: "Capacidades integradas (saber-hacer-ser) frente a habilidades aisladas.", ref: "Perrenoud, 2001" },
    ],
    image: portadaAnalitica,
    color: "#0B0B0B",
  },
];

// Pilares de la landing = las 5 competencias (escaparate). `key` sigue apuntando
// a la disciplina/constelación de origen; solo cambia cómo se nombran de cara al alumno.
// LAS CINCO COMPETENCIAS, y las MATERIAS que las entrenan.
//
// La distinción está en `docs/estado/glosario.md` y no es cosmética: una competencia es el
// paraguas y agrupa VARIAS materias —Pensamiento crítico cubre Filosofía y también Ética—,
// mientras que `key` sigue apuntando al curso que hoy la representa en el catálogo.
//
// Las dos listas hacen falta en la portada y hacen dos trabajos distintos: la competencia es
// lo que vende el método, y la materia es lo que un padre o un alumno busca para saber si
// esto le sirve para aprobar lo que tiene en el instituto. Enseñar solo una de las dos deja
// fuera a la mitad de quien lee.
export const PILLARS = [
  { key: "filosofia", label: "Pensamiento crítico", description: "Lógica, argumentación y filosofía para pensar mejor.",
    asignaturas: ["Filosofía", "Ética"], familia: "Filosofía" },
  { key: "literatura", label: "Comunicación", description: "Escritura, literatura, storytelling y oratoria.",
    // INGLES ENTRA EL 18 DE OCTUBRE DE 2026, y lo pidio Gabri para la portada. Es la Lengua
    // Extranjera del curriculo y hay seis cursos publicados, de A1 a C2; faltaba en la lista
    // de materias solo porque esta tabla se escribio antes que ellos.
    // «CASTELLANO, INGLES, ALEMAN» DESDE EL 20 DE OCTUBRE DE 2026, y lo pidio Gabri viendo
    // la tarjeta: *«se ve raro que el subtitulo ocupe dos lineas: debe ocupar solo una»*.
    // «Lengua y Literatura Castellanas» es el nombre del BOE y mide cuatro palabras, asi
    // que partia la linea el solo. Y entra el aleman, que llevaba seis cursos publicados
    // -de A1 a C2- sin estar en esta lista.
    asignaturas: ["Castellano", "Inglés", "Alemán"], familia: "Comunicación" },
  { key: "psicologia", label: "Autocomprensión", description: "Psicología, hábitos y bienestar para conocerte.",
    asignaturas: ["Psicología"], familia: "Psicología" },
  { key: "musica", label: "Creatividad", description: "Música, creatividad e improvisación.",
    asignaturas: ["Música"], familia: "Música" },
  { key: "cultura-clasica", label: "Cultura y política", description: "Mitología, Grecia, Roma, historia de las ideas y política.",
    asignaturas: ["Latín", "Cultura Clásica"], familia: "Cultura Clásica" },
];

/** Todas las materias, en el orden de sus competencias. Para la portada. */
export const ASIGNATURAS = PILLARS.flatMap((p) => p.asignaturas);
